import React, {useState, useEffect, useContext} from 'react'
import {useIntl} from 'react-intl'
import {PageTitle} from '../../../../../../_metronic/layout/core'
import NewPostCodeModal from '../../../../../../_metronic/partials/modals/NewPostCodeModal'
import DeleteModal from '../../../../../../_metronic/partials/modals/DeleteModal'
import {formatDate, moneyFormatter} from '../../../../../../utils'
import '../LookUpTables.css'
import {
  getPostCodePaginated,
  createPostCode,
  updatePostCode,
  deletePostCode,
  getAllPostCodesForExport
} from '../../../../../api/post-code-api'
import { AdminContext } from '../../../../../context/AdminContext'
import {UploadLoader} from './Upload/UploadLoader'
import UploadPostCode from './Upload/UploadPostCode'
import { useLocation } from 'react-router-dom'

function PostCodeMatrix() {
  const { search } = useLocation()
  const intl = useIntl()
  const [data, setData] = useState<any>({})
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [dataPerPage] = useState(10)
  const [postCodeData, setPostCodeData] = useState<any>({})
  const [isAddingOrUpdatingPostCode, setIsAddingOrUpdatingPostCode] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | undefined>()
  const [successMsg, setSuccessMsg] = useState<string | undefined>()
  const [deleteMessage, setDeleteMessage] = useState<string | undefined>()
  const [isDeletingPostCode, setIsDeletingPostCode] = useState(false)
  const [isPerformingDelete, setIsPerformingDelete] = useState(false)
  const [PostCodeFilter, setPostCodeFilter] = useState('')
  const [exportProgress, setExportProgress] = useState(0)

  type ApiResponse = Record<string, any>
  const XLSX = require('xlsx')

  const {getPriceBooksAsync, priceBooks, isPostCodeUploading, pollActivePostCodeImport} =
  useContext(AdminContext)

  const postCodeFields = [
    { label: 'Post Code', key: 'postCode', type: 'number' },
    { label: 'Price Book', key: 'pricebookId', type: 'dropdown', options: priceBooks},
    { label: 'Delivery Free', key: 'deliveryFee', type: 'number' },
  ]
  
  useEffect(() => {
    if (!isPostCodeUploading) pollActivePostCodeImport();

    if (!isPostCodeUploading) {
      const intervalId = setInterval(() => {
        pollActivePostCodeImport();
      }, 5000);
  
      return () => clearInterval(intervalId);
    }
  }, [isPostCodeUploading]);

  const handleErrorMessage = (errorMsg: string) => {
    setSuccessMsg("")
    setErrorMsg(errorMsg)
  }

  const handleSuccessMessage= (successMsg: string) => {
    setErrorMsg("")
    setSuccessMsg(successMsg)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setErrorMsg("")
      setSuccessMsg("")
    }, 3000)
  
    return () => clearTimeout(timer)
  }, [errorMsg, successMsg])

  useEffect(() => {
    fetchPostCodeMatrices(currentPage)
  }, [currentPage])

  useEffect(() => {
    handlePostCodeFilter(PostCodeFilter)
  }, [PostCodeFilter])

  useEffect(() => {
    if (!priceBooks)
      getPriceBooksAsync()
  }, [])

  const fetchPostCodeMatrices = async (currentPage: number) => {
    try {
      await getPostCodePaginated(currentPage, dataPerPage, PostCodeFilter).then((response: any) => {
        setData(response.data)
        setTotalPages(Math.ceil(response.data.totalCount / 10))
      })
    } catch (error) {
      console.error('Error fetching data:', error)
    }
  }

  const handlePrevPage = () => {
    setCurrentPage((prevPage) => prevPage - 1)
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1)
  }

  const handlePostCodeFilter = async (keyWord : any) => {
    setPostCodeFilter(keyWord)
    fetchPostCodeMatrices(currentPage)
  }

  const handleSavePostCode = async (newPostCode: any) => {
    if (postCodeData.Id != null) {
      newPostCode.Id = postCodeData.Id
      updatePostCode(newPostCode)
      .then((result: any) => {
        if (result.data) {
          handleSuccessMessage("Post Code updated successfully.")
          fetchPostCodeMatrices(currentPage)
        } else {
          handleErrorMessage(result?.response?.data)
        }
      })
      .catch(() => {
        handleErrorMessage('Error encountered while saving. Please try again later.')
      })
    }else{
      createPostCode(newPostCode)
      .then((result: any) => {
        if (result.data) {
          handleSuccessMessage("Post Code added successfully.")
          fetchPostCodeMatrices(currentPage)
        } else {
          handleErrorMessage(result?.response?.data)
        }
      })
      .catch(() => {
        handleErrorMessage('Error encountered while saving. Please try again later.')
      })
    }
  }

  const handleAddClick = () => {
    setErrorMsg("")
    setSuccessMsg("")
    setPostCodeData({})
    setIsDeletingPostCode(false)
    setIsAddingOrUpdatingPostCode(true)
  }

  const handleEditClick = (id: any, postCode: any, pricebookId: any, pricebookName: any, deliveryFee: any) => {
    setErrorMsg("")
    setSuccessMsg("")
    setPostCodeData({Id: id, postCode: postCode, pricebookId: pricebookId, pricebookName: pricebookName, deliveryFee: deliveryFee})
    setIsDeletingPostCode(false)
    setIsAddingOrUpdatingPostCode(true)
  }

  const handleDeleteClick = (id: any, name: any) => {
    setErrorMsg("")
    setSuccessMsg("")
    setPostCodeData({Id: id, Name: name})
    setDeleteMessage(`This will remove the Post Code ${name} from the system. Continue?`)
    setIsDeletingPostCode(true)
    setIsAddingOrUpdatingPostCode(false)
  }

  const handleExport = () => {
    generateExcel()
  }

  const handleDeletePostCodeCancel = () => {
    setIsDeletingPostCode(false)
    setIsPerformingDelete(false)
  }

  const handleDeletePostCode = () => {
    setIsPerformingDelete(true)
    deletePostCode(postCodeData.Id)
      .then((result: any) => {
        if (result.data) {
          handleSuccessMessage("Post Code deleted successfully.")
          fetchPostCodeMatrices(currentPage)
        } else {
          handleErrorMessage(result?.response?.data)
        }
      })
      .catch(() => {
        handleErrorMessage('Error encountered while saving. Please try again later.')
      })
      .finally(() => {
        handleDeletePostCodeCancel()
      })
  }

  const handleClosePostCodeModal = () => {
    setIsAddingOrUpdatingPostCode(false)
  }

  const generateExcel = async () => {
    const workbook = XLSX.utils.book_new()
    const totalApis = 1
    setExportProgress(10)
  
    const fetchData = async (apiFunction: () => Promise<{ data: ApiResponse[], headers: Record<string, string> }>, sheetName: string): Promise<void> => {
      try {
        const response = await apiFunction()
  
        if (!response.data || response.data.length === 0) {
          console.error(`No data returned for ${sheetName}.`)
          return
        }
  
        const headers = response.headers
        const headerRow = Object.values(headers)
  
        const sheetData = [
          headerRow,
          ...response.data.map(item =>
            headerRow.map(header => {
              const key = Object.keys(headers).find(key => headers[key].toLowerCase() === header.toLowerCase())
              const value = key ? item[key] : ''

              if (value === null || value === undefined) {
                return value
              } else if (typeof value === 'number') {
                return value
              } else {
                return String(value)
              }
            })
          )
        ]
  
        const sheet = XLSX.utils.aoa_to_sheet(sheetData)
        XLSX.utils.book_append_sheet(workbook, sheet, sheetName)
        setExportProgress(prevProgress => prevProgress + (100 / totalApis))
      } catch (error) {
        console.error(`Error processing ${sheetName}:`, error)
      }
    }
  
    const apis = [
      getAllPostCodesForExport,
    ]
  
    const sheetNames = [
      'PostCode',
    ]
  
    for (let i = 0; i < apis.length; i++) {
      await fetchData(apis[i], sheetNames[i])
    }
  
    const dateStr = formatDate(new Date())
    const fileName = `Postcode Matrix Export_${dateStr}.xlsx`
    XLSX.writeFile(workbook, fileName)
    setExportProgress(100)
    console.log(`Excel file "${fileName}" generated successfully!`)
  }

  interface ExportProgressBarProps {
    progress: number
  }

  const ExportProgressBar: React.FC<ExportProgressBarProps> = ({ progress }) => {
    return progress < 100 ? (
      <div className='col-8'>
        <div className="progress">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{ width: `${progress}%` }}
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {progress.toFixed(2)}% Complete
          </div>
        </div>
      </div>
    ) : null;
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({id: 'MENU.POSTCODEMATRIX'})}</PageTitle>
      <UploadLoader></UploadLoader>
      <ExportProgressBar progress={exportProgress} />
      {/* BUTTONS */}
      <div className='mb-5'>
        <div className='d-flex justify-content-end'>
            <UploadPostCode></UploadPostCode>
          <div className='ms-2'></div>
          <button disabled={isPostCodeUploading} className='btn btn-primary' onClick={handleExport}>
            EXPORT
          </button>
        </div>
      </div>
      <div className='row'>
        <div className='col-sm-2'>
          <button className='btn btn-primary btn-sm' onClick={handleAddClick}><i className='fa fa-plus text-dark'></i> New Post Code</button>
        </div>
        <div className='col-sm-7'>
          {errorMsg && (
            <div className='alert alert-danger m-auto text-center' role='alert'>
              <i className='fa fa-exclamation-circle text-danger'></i> {errorMsg}
            </div>
          )}
          {successMsg && (
            <div className='alert alert-success m-auto text-center' role='alert'>
              <i className='fa fa-check-circle text-success'></i> {successMsg}
            </div>
          )}
        </div>
        <div className='col-sm-3'>
          <input type='text' placeholder='Search' className='form-control' value={PostCodeFilter} onChange={(e) => setPostCodeFilter(e.target.value)}></input>
        </div>
      </div>
      
      {/* TABLE */}
      <div className='m-auto mt-4'>
        <table className='table table-hover table-striped lookup-table' style={{backgroundColor: '#fff'}}>
          <thead className='thead-dark'>
            <tr>
              {/* <th scope='col'>ID</th> */}
              <th scope='col' className='align-middle text-center'>Post Code</th>
              <th scope='col' className='align-middle text-center'>Price Book</th>
              <th scope='col' className='align-middle text-center'>Delivery Fee</th>
              <th scope='col' className='align-middle' style={{width: '1px'}}></th>
              <th scope='col' className='align-middle' style={{width: '1px'}}></th>
            </tr>
          </thead>
          <tbody>
            {data?.items?.map((data: any) => (
              <tr key={data?.id}>
                {/* <td>{data?.id}</td> */}
                <td className='align-middle text-center'>{data?.postCode}</td>
                <td className='align-middle text-center'>{data?.pricebookName}</td>
                <td className='align-middle text-center'>{moneyFormatter.format(data?.deliveryFee)}</td>
                <td className='align-middle'>
                  <button className='btn btn-primary btn-sm' onClick={() => handleEditClick(data?.id, data?.postCode, data?.pricebookId, data?.pricebookName, data?.deliveryFee)}>Edit</button>
                </td>
                <td className='align-middle'>
                  <button className='btn btn-danger btn-sm' onClick={() => handleDeleteClick(data?.id, data?.postCode)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className='pagination d-flex align-items-center pb-4'>
          <button className='page-link' onClick={handlePrevPage} disabled={currentPage === 1}>
            <i className='bi bi-chevron-left'></i>
          </button>
          <div className='px-4'>{`Page ${currentPage} of ${totalPages}`}</div>
          <button
            className='page-link'
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            <i className='bi bi-chevron-right'></i>
          </button>
        </div>
      </div>
      <NewPostCodeModal
        isOpen={isAddingOrUpdatingPostCode}
        onClose={handleClosePostCodeModal}
        onSave={handleSavePostCode}
        fields={postCodeFields}
        initialValues={postCodeData}
      />
      <DeleteModal
        isModalOpen={isDeletingPostCode}
        handleConfirmDelete={handleDeletePostCode}
        handleCancelDelete={handleDeletePostCodeCancel}
        isDeleting={isPerformingDelete}
        title={deleteMessage}
      />
    </>
  )
}

export default PostCodeMatrix
